import { useState } from "react";
import { CreditCard, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, Empty, SimpleSelect, payStatus } from "@/components/common";
import { ReceiptDialog, openCheckout, startSubscription } from "@/components/dialogs";
import { usePortal } from "@/components/PortalLayout";
import { useApi } from "@/lib/useApi";
import { brl, dt, dtTime, todayISO, METHOD_LABEL, CHANNEL_LABEL } from "@/lib/fmt";

export default function PortalFinance() {
  const { sid } = usePortal();
  const [o] = useApi(`/portal/students/${sid}`, [sid]);
  const [d] = useApi(`/portal/students/${sid}/payments`, [sid]);
  const [plans] = useApi("/plans");
  const [plan, setPlan] = useState("");
  const [receipt, setReceipt] = useState(null);
  const t = todayISO();
  if (!o || !d) return <Empty text="Carregando…" />;
  const sub = o.subscription;
  const open = d.payments.filter((p) => ["pendente", "recusado"].includes(p.status));
  const recurring = (plans || []).filter((p) => p.billing === "recorrente");
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-extrabold uppercase">Financeiro</h1>
      <div className="panel p-5 space-y-2" data-testid="portal-plan-box">
        <div className="eyebrow">Plano contratado</div>
        <div className="font-display text-2xl font-bold uppercase">{o.student.plan_name || "—"}</div>
        <div className="text-sm text-slate-600">Vigência {dt(o.student.plan_start)} → {dt(o.student.plan_end)} · mensalidade {brl(o.student.monthly_fee)} · vence dia {o.student.due_day}</div>
        {sub ? <div className="rounded-lg bg-emerald-50 p-3 text-sm" data-testid="portal-subscription">Assinatura no cartão <StatusBadge status={sub.status} /> · próxima cobrança {dtTime(sub.current_period_end)} {sub.cancel_at_period_end && "· será encerrada ao fim do período"}</div>
          : <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <SimpleSelect value={plan} onChange={setPlan} placeholder="Escolha um plano para assinar" testid="portal-plan-select" options={recurring.map((p) => ({ value: p.id, label: `${p.name} · ${brl(p.price)}` }))} />
            <Button disabled={!plan} onClick={() => startSubscription(sid, plan)} className="bg-zinc-900" data-testid="portal-subscribe-btn"><CreditCard className="w-4 h-4 mr-1" />Assinar no cartão</Button>
          </div>}
      </div>
      <div className="panel p-5" data-testid="portal-open-list">
        <div className="font-display text-lg font-bold uppercase mb-2">Em aberto</div>
        {!open.length ? <Empty text="Tudo em dia. Oss!" /> : open.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 py-3 border-t border-slate-100" data-testid={`portal-open-${p.id}`}>
            <div><div className="text-sm font-semibold">{p.description}</div><div className="text-xs text-slate-500">vence {dt(p.due_date)} · <StatusBadge status={payStatus(p, t)} /></div>
              {p.failure_reason && <div className="text-xs text-red-600">{p.failure_reason}</div>}</div>
            <div className="text-right"><div className="font-bold">{brl(p.amount)}</div>
              <Button size="sm" onClick={() => openCheckout(p.id)} className="mt-1 bg-red-600 hover:bg-red-700" data-testid={`portal-pay-${p.id}`}>Pagar com cartão</Button></div>
          </div>))}
        <p className="text-xs text-slate-500 mt-2">Pagamentos em Pix ou dinheiro são lançados pela recepção após a confirmação.</p>
      </div>
      <div className="panel p-5" data-testid="portal-history">
        <div className="font-display text-lg font-bold uppercase mb-2">Histórico</div>
        {d.payments.filter((p) => !["pendente", "recusado"].includes(p.status)).map((p) => (
          <div key={p.id} className="flex items-center justify-between py-2.5 border-t border-slate-100 text-sm">
            <div><div className="font-semibold">{p.description}</div><div className="text-xs text-slate-500">{CHANNEL_LABEL[p.channel] || ""} · {METHOD_LABEL[p.method] || ""} · {dt(p.paid_at || p.due_date)}</div></div>
            <div className="flex items-center gap-2"><b>{brl(p.amount)}</b><StatusBadge status={p.status} />
              {["aprovado", "estornado"].includes(p.status) && <button onClick={() => setReceipt(p.id)} className="text-slate-400 hover:text-red-600" data-testid={`portal-receipt-${p.id}`}><Receipt className="w-4 h-4" /></button>}</div>
          </div>))}
      </div>
      {receipt && <ReceiptDialog paymentId={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
